// Hedef gösterge seçici — arama listesi (M16 T11). Set göstergeleri (map/reported) ya da
// katalog kodları (create). Klavye: ↓/↑ gezinme, Enter seç, Esc kapat; role=combobox/listbox.
// Türetilmiş hedef "ƒx hesaplanır" rozetiyle (Eng 3A); seçim engellenmez (plan atlar + uyarır).

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { inputStyle } from "../kpi-form-styles";

export interface TargetOption {
  code: string;
  label: string;
  unit: string | null;
  isDerived: boolean;
}

export interface TargetPickerProps {
  value: string | undefined;
  options: readonly TargetOption[];
  onChange: (code: string | null) => void;
  ariaLabel: string;
  disabled?: boolean;
}

const MAX_RESULTS = 30;

function fold(s: string): string {
  return s.toLocaleLowerCase("tr");
}

export function TargetPicker({
  value,
  options,
  onChange,
  ariaLabel,
  disabled,
}: TargetPickerProps): JSX.Element {
  const selected = useMemo(() => options.find((o) => o.code === value), [options, value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const results = useMemo(() => {
    const q = fold(query.trim());
    const list = q
      ? options.filter((o) => fold(o.code).includes(q) || fold(o.label).includes(q))
      : options;
    return list.slice(0, MAX_RESULTS);
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(code: string): void {
    onChange(code);
    setQuery("");
    setOpen(false);
  }

  const display = open ? query : (selected?.label ?? value ?? "");

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 220 }}>
      <input
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        disabled={disabled}
        placeholder={KPI_IMPORT_COPY.mapping.targetPlaceholder}
        value={display}
        style={{
          ...inputStyle,
          minHeight: 36,
          padding: "6px 10px",
          ...(value && !selected ? { borderColor: "var(--color-warning-border)" } : {}),
        }}
        onFocus={() => {
          setOpen(true);
          setCursor(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setCursor(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setCursor((c) => Math.min(results.length - 1, c + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((c) => Math.max(0, c - 1));
          } else if (e.key === "Enter") {
            const r = results[cursor];
            if (open && r) {
              e.preventDefault();
              pick(r.code);
            }
          } else if (e.key === "Escape") {
            setOpen(false);
            setQuery("");
          } else if (e.key === "Backspace" && query === "" && value) {
            onChange(null);
          }
        }}
      />
      {selected ? (
        <div style={{ fontSize: "0.7rem", color: "var(--color-text-3)", marginTop: 2 }}>
          <code>{selected.code}</code>
          {selected.unit ? ` · ${selected.unit}` : ""}
          {selected.isDerived ? ` · ${KPI_IMPORT_COPY.mapping.derivedHint}` : ""}
        </div>
      ) : value ? (
        <div style={{ fontSize: "0.7rem", color: "var(--color-warning-text)", marginTop: 2 }}>
          <code>{value}</code> — listede yok
        </div>
      ) : null}
      {open ? (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 20,
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: "auto",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            marginTop: 2,
            padding: 4,
            listStyle: "none",
            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
          }}
        >
          {results.length === 0 ? (
            <li style={{ padding: "6px 8px", fontSize: "0.8rem", color: "var(--color-text-3)" }}>
              {KPI_IMPORT_COPY.mapping.targetNoResult}
            </li>
          ) : (
            results.map((o, i) => (
              <li
                key={o.code}
                role="option"
                aria-selected={o.code === value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o.code);
                }}
                onMouseEnter={() => setCursor(i)}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  background:
                    i === cursor
                      ? "color-mix(in srgb, var(--color-primary) 8%, white)"
                      : "transparent",
                }}
              >
                <div style={{ color: "var(--color-text)" }}>
                  {o.label}
                  {o.isDerived ? (
                    <span
                      style={{ color: "var(--color-text-3)", marginLeft: 6, fontSize: "0.72rem" }}
                    >
                      {KPI_IMPORT_COPY.mapping.derivedHint}
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--color-text-3)" }}>
                  <code>{o.code}</code>
                  {o.unit ? ` · ${o.unit}` : ""}
                </div>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
